<template>
  <div class="time-transform-editor">
    <b-field>
      <b-switch
        v-model="transformEnabled"
        type="is-info">
        Apply additional transforms to the time range
      </b-switch>
    </b-field>

    <div
      v-if="transformEnabled"
      class="transform-summary mt-3">
      <div class="summary-text">
        <div
          v-if="offsetDays !== 0"
          class="summary-line">
          Offset the time range by
          <strong>{{ Math.abs(offsetDays) }} days</strong>
          {{ offsetDays > 0 ? 'forward' : 'backward' }}
        </div>
        <div
          v-if="repeatConfig"
          class="summary-line">
          Repeat the time range
          <strong>{{ repeatConfig.count }} times</strong>
          every <strong>{{ Math.abs(repeatConfig.period) }} days</strong>
          {{ repeatConfig.period > 0 ? 'forward' : 'backward' }}
        </div>
        <div
          v-if="offsetDays === 0 && !repeatConfig"
          class="summary-line summary-empty">
          No transforms configured
        </div>
      </div>
      <b-button @click="modalOpen = true">
        Edit
      </b-button>
    </div>

    <b-modal
      v-model="modalOpen"
      :width="620"
      :can-cancel="['escape', 'x']"
      has-modal-card
      trap-focus>
      <div class="modal-card transform-modal">
        <section class="modal-card-body">
          <div class="transform-modal-title">
            Time Range Transforms
          </div>

          <div class="transform-layout">
            <div class="transform-controls">
              <div class="control-section">
                <div class="control-header">
                  <b-switch
                    v-model="pendingOffsetEnabled"
                    type="is-info">
                    Offset
                  </b-switch>
                  <b-tooltip
                    type="is-primary"
                    position="is-right"
                    multilined
                    size="is-medium">
                    <template #content>
                      Shift the entire time range forward or backward
                      by a fixed number of days.
                    </template>
                    <span class="info-icon">
                      <i class="material-icons">info</i>
                    </span>
                  </b-tooltip>
                </div>
                <template v-if="pendingOffsetEnabled">
                  <b-field
                    label="Days"
                    class="mt-3">
                    <b-numberinput
                      v-model="pendingOffset"
                      size="is-small"
                      controls-alignment="right" />
                  </b-field>
                </template>
              </div>

              <hr />

              <div class="control-section">
                <div class="control-header">
                  <b-switch
                    v-model="pendingRepeatEnabled"
                    type="is-info">
                    Repeat
                  </b-switch>
                  <b-tooltip
                    type="is-primary"
                    position="is-right"
                    multilined
                    size="is-medium">
                    <template #content>
                      Duplicate the time range multiple times, each shifted
                      by a fixed period. Useful for comparing across weeks or months.
                    </template>
                    <span class="info-icon">
                      <i class="material-icons">info</i>
                    </span>
                  </b-tooltip>
                </div>
                <template v-if="pendingRepeatEnabled">
                  <b-field
                    label="Count"
                    class="mt-3">
                    <b-numberinput
                      v-model="pendingRepeatCount"
                      :min="1"
                      size="is-small"
                      controls-alignment="right" />
                  </b-field>
                  <b-field label="Period (days)">
                    <b-numberinput
                      v-model="pendingRepeatPeriod"
                      size="is-small"
                      controls-alignment="right" />
                  </b-field>

                  <div
                    v-if="hasOverlapWarning"
                    class="overlap-warning mt-3">
                    <b-icon
                      icon="alert"
                      size="is-small" />
                    <span>
                      The repeat period ({{ Math.abs(pendingRepeatPeriod) }} days) is less
                      than the maximum time range duration ({{ warningDurationDays }} days). This causes
                      overlapping date ranges and may result in double-counting data.
                    </span>
                  </div>
                </template>
              </div>
            </div>

            <div class="transform-preview">
              <div class="preview-label">
                Preview
              </div>
              <b-datepicker
                :value="null"
                :events="calendarEvents"
                indicators="bars"
                :day-names="['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa',]"
                inline
                :focused-date="previewFocusDate"
                class="preview-calendar" />
              <div class="preview-legend mt-2">
                <div class="legend-item">
                  <span class="legend-dot legend-dot--primary" />
                  <span>Original range</span>
                </div>
                <div
                  v-if="pendingOffsetEnabled && pendingOffset !== 0"
                  class="legend-item">
                  <span class="legend-dot legend-dot--info" />
                  <span>After offset</span>
                </div>
                <div
                  v-if="pendingRepeatEnabled"
                  class="legend-item">
                  <span class="legend-dot legend-dot--warning" />
                  <span>Repetitions</span>
                </div>
              </div>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <b-button
            expanded
            @click="modalOpen = false">
            Discard
          </b-button>
          <b-button
            expanded
            type="is-primary"
            @click="applyTransform">
            Apply
          </b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import {
    addDays,
    daysBetween,
    computePresetRange,
    computeMaxDurationDays
  } from './timeRangeUtils'

  function dateRange (start, end) {
    const dates = []
    const d = new Date(start)
    while (d <= end) {
      dates.push(new Date(d))
      d.setDate(d.getDate() + 1)
    }
    return dates
  }

  export default {
    name: 'TimeTransformEditor',
    props: {
      offsetDays: {
        type: Number,
        default: 0,
      },
      repeatConfig: {
        type: Object,
        default: null,
      },
      effectiveTimeRange: {
        type: Object,
        default: null,
      },
      maxDurationDays: {
        type: Number,
        default: null,
      },
      exampleReportDate: {
        type: Date,
        default: null,
      },
    },
    data () {
      return {
        modalOpen: false,
        manuallyEnabled: false,
        pendingOffsetEnabled: false,
        pendingOffset: 0,
        pendingRepeatEnabled: false,
        pendingRepeatCount: 1,
        pendingRepeatPeriod: -7,
      }
    },
    computed: {
      transformEnabled: {
        get () {
          return this.manuallyEnabled || this.offsetDays !== 0 || this.repeatConfig !== null
        },
        set (enabled) {
          this.manuallyEnabled = enabled
          if (!enabled) {
            this.$emit('update:offsetDays', 0)
            this.$emit('update:repeatConfig', null)
          }
        },
      },

      availableDates () {
        return this.$store?.getters['datePicker/availableDates'] || []
      },
      resolvedReportDate () {
        if (this.exampleReportDate) return this.exampleReportDate
        const d = new Date()
        d.setDate(d.getDate() - 1)
        d.setHours(0, 0, 0, 0)
        return d
      },
      computedRange () {
        const tr = this.effectiveTimeRange
        if (!tr) return null
        if (tr.type === 'RelativeDateRange') {
          const end = addDays(this.resolvedReportDate, tr.offset_days || 0)
          const start = addDays(end, -(tr.duration_days || 1) + 1)
          return [start, end,]
        }
        if (tr.key) {
          return computePresetRange(tr.key, this.resolvedReportDate, this.availableDates)
        }
        return null
      },
      baseStart () {
        if (this.computedRange) return this.computedRange[0]
        const d = new Date(this.resolvedReportDate)
        const day = d.getDay()
        const diff = day === 0 ? 6 : day - 1
        d.setDate(d.getDate() - diff)
        return d
      },
      baseEnd () {
        if (this.computedRange) return this.computedRange[1]
        return addDays(this.baseStart, 6)
      },
      baseRangeDays () {
        return daysBetween(this.baseStart, this.baseEnd) + 1
      },
      warningDurationDays () {
        if (this.maxDurationDays) return this.maxDurationDays
        const tr = this.effectiveTimeRange
        if (tr?.key) {
          const maxDays = computeMaxDurationDays(
            tr.key, this.resolvedReportDate, this.availableDates
          )
          if (maxDays) return maxDays
        }
        if (tr?.type === 'RelativeDateRange') {
          return tr.duration_days || this.baseRangeDays
        }
        return this.baseRangeDays
      },

      previewFocusDate () {
        return this.baseStart
      },

      hasOverlapWarning () {
        if (!this.pendingRepeatEnabled) return false
        return Math.abs(this.pendingRepeatPeriod) < this.warningDurationDays
      },

      calendarEvents () {
        const events = []

        const baseStart = this.baseStart
        const baseEnd = this.baseEnd

        dateRange(baseStart, baseEnd).forEach(d => {
          events.push({ date: new Date(d), type: 'is-primary', })
        })

        if (this.pendingOffsetEnabled && this.pendingOffset !== 0) {
          const offsetStart = addDays(baseStart, this.pendingOffset)
          const offsetEnd = addDays(baseEnd, this.pendingOffset)
          dateRange(offsetStart, offsetEnd).forEach(d => {
            events.push({ date: new Date(d), type: 'is-info', })
          })
        }

        if (this.pendingRepeatEnabled && this.pendingRepeatCount > 0 && this.pendingRepeatPeriod !== 0) {
          const hasOffset = this.pendingOffsetEnabled && this.pendingOffset !== 0
          const effectiveStart = hasOffset
            ? addDays(baseStart, this.pendingOffset)
            : baseStart
          const effectiveEnd = hasOffset
            ? addDays(baseEnd, this.pendingOffset)
            : baseEnd

          for (let i = 1; i <= this.pendingRepeatCount; i++) {
            const repStart = addDays(effectiveStart, this.pendingRepeatPeriod * i)
            const repEnd = addDays(effectiveEnd, this.pendingRepeatPeriod * i)
            dateRange(repStart, repEnd).forEach(d => {
              events.push({ date: new Date(d), type: 'is-warning', })
            })
          }
        }

        return events
      },
    },
    watch: {
      modalOpen (open) {
        if (open) {
          this.pendingOffsetEnabled = (this.offsetDays || 0) !== 0
          this.pendingOffset = this.offsetDays || 0
          this.pendingRepeatEnabled = this.repeatConfig !== null
          this.pendingRepeatCount = this.repeatConfig?.count || 1
          this.pendingRepeatPeriod = this.repeatConfig?.period || -7
        }
      },
    },
    methods: {
      applyTransform () {
        this.$emit('update:offsetDays', this.pendingOffsetEnabled ? this.pendingOffset : 0)
        if (this.pendingRepeatEnabled) {
          this.$emit('update:repeatConfig', {
            count: this.pendingRepeatCount,
            period: this.pendingRepeatPeriod,
          })
        } else {
          this.$emit('update:repeatConfig', null)
        }
        this.modalOpen = false
      },
    },
  }
</script>

<style lang="scss" scoped>
.info-icon {
  cursor: help;
  display: inline-flex;
  align-items: center;

  .material-icons {
    font-size: 16px;
    color: #888;
  }
}

.transform-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px;
  background: #fafafa;

  .summary-text {
    flex: 1;
  }

  .summary-line {
    font-size: 13px;
    color: #444;
    line-height: 1.6;
  }

  .summary-empty {
    color: #999;
    font-style: italic;
  }

  .button {
    flex-shrink: 0;
  }
}

.transform-modal {
  background: white;
  border-radius: 8px;

  .transform-modal-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 16px;
  }

  .modal-card-body {
    padding: 20px;
  }

  .modal-card-foot {
    background: $grayscale-8;
    display: flex;
    padding: 16px;
    gap: 8px;
  }
}

.transform-layout {
  display: flex;
  gap: 20px;

  .transform-controls {
    flex: 1;
    min-width: 0;

    .control-header {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    hr {
      margin: 16px 0;
      border: none;
      border-top: 1px solid #eee;
    }
  }

  .transform-preview {
    flex: 0 0 auto;

    .preview-label {
      font-size: 12px;
      font-weight: 500;
      color: #666;
      margin-bottom: 4px;
    }

    ::v-deep .datepicker {
      .dropdown-content {
        box-shadow: none;
        padding: 0;
        width: fit-content;
      }

      .dropdown-menu {
        width: fit-content;
        min-width: 0;
      }

      .datepicker-cell.is-today {
        border: none !important;
      }

      .datepicker-cell.is-unselectable {
        cursor: default;
      }
    }
  }
}

.preview-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  .legend-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #666;
  }

  .legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;

    &--primary {
      background: $primary;
    }

    &--info {
      background: #167df0;
    }

    &--warning {
      background: #D31B5E;
    }
  }
}

.overlap-warning {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
  font-size: 12px;
  color: #665200;
}
</style>
